import { useState, useEffect } from 'react'
import { TextInput } from '../../inputs'
import { LocationOnOutlined } from '@mui/icons-material'
import { usePlacesWidget } from 'react-google-autocomplete'
import { getDrupalSetting } from '@ggs/utils'
import { useI18n } from '@ggs/i18n'

/**
 * @typedef {import('react-hook-form').FieldErrors} FieldErrors
 * @typedef {import('react-hook-form').FieldValues} FieldValues
 * @typedef {import('react-hook-form').Control<FieldValues>} FormControl
 * @typedef {import('react-hook-form').UseFormRegister<FieldValues>} FormRegister
 */

/**
 * @typedef {Object} LocationFieldProps
 * @property {FieldErrors} errors
 * @property {FormRegister} register
 * @property {function} setValueCallback
 * @property {function} resetValueCallback
 * @property {function} errorCallback
 * @property {function} clearErrorCallback
 * @property {function} getValues
 */

/**
 * Provides location field form element.
 * @param {LocationFieldProps} props
 * @return {JSX.Element}
 */
const LocationField = ({
  register,
  errors,
  setValueCallback,
  resetValueCallback,
  errorCallback,
  clearErrorCallback,
  getValues
}) => {
  const { t } = useI18n()
  const errorMessage = errors.address?.message
  const hasError = Boolean(errors.address)

  // Add autocomplete to the address field
  const { ref: placesRef } = usePlacesWidget({
    apiKey: getDrupalSetting('mapsApiKey') || `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
    onPlaceSelected: (place) => {
      setValueCallback('address', place.formatted_address)
      clearErrorCallback('address')
    },
    inputAutocompleteValue: 'off',
    options: {
      types: [],
      componentRestrictions: {
        country: ['ca'],
      },
    },
  })
  const [locationFields, setLocationFields] = useState(null)
  // const { ref: locationRef, ...locationFields } = register("location");
  // useImperativeHandle(locationRef, () => placesRef.current);
  useEffect(() => {
    const { ref, ...fields } = register('address')
    setLocationFields(fields)
  }, [])

  /**
   * Disable enter key for location field because the autocomplete selection takes priority
   * @param {KeyboardEvent} e
   */
  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter' && getValues('address')) e.preventDefault()
  }


  return (
    <TextInput
      id="form-quick-search-location"
      label={t('Where Are You Located?')}
      labelClasses={{
        root: 'quick-search-form__form-field-label',
      }}
      placeholder={t('Input address, name of community, or postal code')}
      error={hasError}
      // @ts-ignore
      errorHelperText={errorMessage}
      // @ts-ignore
      startIcon={<LocationOnOutlined color="tertiary" />}
      inputFields={{
        autoComplete: 'off',
        inputRef: placesRef,
        onKeyDown: handleLocationKeyDown,
        onFocus: (e) => {
          e.target.value = ''
          resetValueCallback('address')
        },
        onBlur: () => {
          if (!getValues('address')) {
            errorCallback('address', { type: 'custom', message: t('Please enter a location') })
          }
        },
        ...locationFields,
      }}
    />
  )
}

export default LocationField
